(() => {
  const stickySelector = '[data-product-sticky-atc]';
  const instances = new WeakMap();

  class StickyAddToCart {
    constructor(sticky) {
      this.sticky = sticky;
      this.targetId = sticky.dataset.productStickyAtcTrigger;
      this.button = sticky.querySelector('[data-product-sticky-atc-button]');
      this.text = sticky.querySelector('[data-product-sticky-atc-text]');
      this.mainButton = null;
      this.frame = null;
      this.mainButtonObserver = null;
      /* PMN : position du bouton principal (au-dessus de l'écran / visible), tenue à jour par un IntersectionObserver */
      this.mainButtonState = null;
      this.mainButtonIntersection = null;

      if (!this.targetId || !this.button) return;

      this.button.addEventListener('click', this.onStickyButtonClick.bind(this));
      window.addEventListener('scroll', this.requestUpdate.bind(this), { passive: true });
      window.addEventListener('resize', this.requestUpdate.bind(this), { passive: true });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId && !this.targetId.endsWith(event.data.sectionId)) return;
          window.setTimeout(() => this.refresh(), 0);
        });
      }

      this.refresh();
      this.sticky.hidden = false;

      /* PMN : packs — libellé mis à jour pendant la saisie de la personnalisation */
      document.addEventListener('pmn-pc:change', () => this.syncButtonState());

      /* Listen to all customisation inputs to update sticky button text in real-time */
      const nameInput = document.querySelector('#prénom-nom-');
      if (nameInput) {
        const form = nameInput.closest('form') || nameInput.form || document;
        const syncHandler = () => {
          this.syncButtonState();
          this.requestUpdate();
        };
        /* Watch both text inputs */
        form.querySelectorAll('#prénom-nom-, #prénom-nom-2').forEach((input) => {
          input.addEventListener('input', syncHandler);
        });
        /* Watch shape changes that toggle required state on the 2nd field */
        const shapeInput = document.getElementById('shapeInput');
        if (shapeInput) {
          shapeInput.addEventListener('change', () => setTimeout(syncHandler, 50));
        }
        document.addEventListener('shape:change', () => setTimeout(syncHandler, 50));
      }
    }

    getMainButton() {
      const nextButton = document.getElementById(this.targetId);

      if (nextButton !== this.mainButton) {
        this.mainButton = nextButton;
        this.observeMainButton();
      }

      return this.mainButton;
    }

    observeMainButton() {
      this.mainButtonObserver?.disconnect();
      this.mainButtonIntersection?.disconnect();
      this.mainButtonState = null;

      if (!this.mainButton) return;

      if ('IntersectionObserver' in window) {
        this.mainButtonIntersection = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          /* Bas du bouton dans la page : sert quand la page saute d'un coup par-dessus le bouton
             (ancre, retour en haut), cas où l'observateur ne signale rien */
          this.mainButtonState = {
            visible: entry.isIntersecting,
            bottom: entry.boundingClientRect.bottom + window.scrollY,
          };
          this.requestUpdate();
        });
        this.mainButtonIntersection.observe(this.mainButton);
      }

      this.mainButtonObserver = new MutationObserver(() => {
        this.syncButtonState();
        this.requestUpdate();
      });

      this.mainButtonObserver.observe(this.mainButton, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    refresh() {
      this.getMainButton();
      this.syncButtonState();
      this.requestUpdate();
    }

    syncButtonState() {
      const mainButton = this.getMainButton();
      if (!mainButton || !this.button) return;

      const disabled = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';
      const label = mainButton.querySelector('span')?.textContent.trim() || mainButton.textContent.trim();

      this.button.toggleAttribute('disabled', disabled);
      this.button.setAttribute('aria-disabled', disabled ? 'true' : 'false');

      /* PMN : modification d'un article du panier (pmn-cart-edit.js) */
      if (window.PMNCartEdit && window.PMNCartEdit.ready && this.text) {
        if (this.text.textContent.trim() !== 'Valider') this.text.textContent = 'Valider';
        return;
      }

      /* PMN : packs — tant que les textes ne sont pas renseignés, le bouton flottant invite à personnaliser */
      const packCustomizer = document.querySelector('[data-pack-customizer]');
      if (packCustomizer && this.text) {
        const packLabel =
          packCustomizer.dataset.complete === 'true' ? label : 'Personnaliser mes étiquettes';
        if (packLabel && this.text.textContent.trim() !== packLabel) {
          this.text.textContent = packLabel;
        }
        return;
      }

      /* --- Dynamic label based on required customisation inputs --- */
      const nameInput = document.querySelector('#prénom-nom-');
      if (nameInput && this.text) {
        const requiredInputs = document.querySelectorAll(
          '#prénom-nom-, #prénom-nom-2'
        );
        const allFilled = Array.from(requiredInputs).every((input) => {
          /* Skip inputs that are not currently required or are disabled */
          if (!input.required || input.disabled) return true;
          return input.value.trim().length > 0;
        });
        /* Libellés courts : le bouton flottant est une carte compacte (nom + prix à gauche) */
        const customLabel = allFilled
          ? 'Ajouter au panier'
          : 'Personnaliser';
        if (this.text.textContent.trim() !== customLabel) {
          this.text.textContent = customLabel;
        }
        return;
      }

      if (label && this.text && this.text.textContent.trim() !== label) {
        this.text.textContent = label;
      }
    }

    shouldShow() {
      const mainButton = this.getMainButton();
      if (!mainButton || !this.button) return false;

      /* PMN : état donné par l'IntersectionObserver (aucune mesure de la page au défilement) */
      const state = this.mainButtonState;
      if (state) {
        if (state.visible) return false;
        if (document.documentElement.hasAttribute('data-pmn-sticky-early')) return window.scrollY > 120;
        return window.scrollY > state.bottom;
      }
      if (this.mainButtonIntersection) return false;

      const rect = mainButton.getBoundingClientRect();
      /* PMN : option (fiches test) — bouton flottant dès le premier défilement, tant que le bouton principal n'est pas à l'écran */
      if (document.documentElement.hasAttribute('data-pmn-sticky-early')) {
        return window.scrollY > 120 && (rect.bottom < 0 || rect.top > window.innerHeight);
      }

      return rect.bottom < 0;
    }

    requestUpdate() {
      if (this.frame) return;

      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.syncButtonState();
        this.sticky.classList.toggle('is-visible', this.shouldShow());
      });
    }

    onStickyButtonClick() {
      const mainButton = this.getMainButton();
      if (!mainButton || mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true') return;

      mainButton.click();
    }
  }

  const initStickyAddToCart = (root = document) => {
    root.querySelectorAll?.(stickySelector).forEach((sticky) => {
      if (instances.has(sticky)) return;
      instances.set(sticky, new StickyAddToCart(sticky));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initStickyAddToCart());
  } else {
    initStickyAddToCart();
  }

  document.addEventListener('product-info:loaded', (event) => initStickyAddToCart(event.target));

  /* ======================================================
   *  Dynamic label for the MAIN (non-sticky) add-to-cart
   *  button on customisation / multiformes pages.
   * ====================================================== */
  const initMainButtonLabel = () => {
    const nameInput = document.querySelector('#prénom-nom-');
    if (!nameInput) return; /* Not a customisation page */

    function getMainButton() {
      return document.querySelector('[id^="ProductSubmitButton-"]');
    }

    function updateMainLabel() {
      const mainButton = getMainButton();
      if (!mainButton) return;
      const mainSpan = mainButton.querySelector('span');
      if (!mainSpan) return;

      /* Don't override if product is sold out / unavailable */
      if (mainButton.disabled) return;

      const requiredInputs = document.querySelectorAll('#prénom-nom-, #prénom-nom-2');
      const allFilled = Array.from(requiredInputs).every((input) => {
        if (!input.required || input.disabled) return true;
        return input.value.trim().length > 0;
      });

      const label = allFilled
        ? 'Ajouter mes étiquettes au panier'
        : 'Personnaliser mes étiquettes';

      if (mainSpan.textContent.trim() !== label) {
        mainSpan.textContent = label;
      }
    }

    /* Watch both text inputs */
    document.querySelectorAll('#prénom-nom-, #prénom-nom-2').forEach((input) => {
      input.addEventListener('input', updateMainLabel);
    });

    /* Watch shape changes that toggle required state on the 2nd field */
    const shapeInput = document.getElementById('shapeInput');
    if (shapeInput) {
      shapeInput.addEventListener('change', () => setTimeout(updateMainLabel, 50));
    }
    document.addEventListener('shape:change', () => setTimeout(updateMainLabel, 50));

    /* Watch variant changes (Globo radios, selects) that reset button text */
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!t) return;
      if (t.closest('variant-selects') || t.closest('.globo-variant-selector')) {
        /* Shopify re-renders the button after a variant change; wait for it */
        setTimeout(updateMainLabel, 150);
        setTimeout(updateMainLabel, 400);
      }
    });

    /* PubSub variant change (Dawn theme) */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        setTimeout(updateMainLabel, 150);
        setTimeout(updateMainLabel, 400);
      });
    }

    /* MutationObserver: catch Shopify overwriting the button text.
       Runs synchronously (before paint) so the user never sees the flash. */
    const observeMainButton = () => {
      const mainButton = getMainButton();
      if (!mainButton) return;

      const observer = new MutationObserver(() => {
        updateMainLabel(); /* sync — guard prevents infinite loop */
      });

      observer.observe(mainButton, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    };

    observeMainButton();

    /* Initial update */
    updateMainLabel();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMainButtonLabel);
  } else {
    initMainButtonLabel();
  }
  /* ======================================================
   *  Sticky ATC: live label preview + quantity
   * ====================================================== */
  const initStickyPreview = () => {
    const nameInput = document.querySelector('#prénom-nom-');
    if (!nameInput) return; /* Not a customisation page */

    const previewContainer = document.querySelector('[data-sticky-atc-preview]');
    const qtyEl = document.querySelector('[data-sticky-atc-qty]');
    const labelEl = document.querySelector('[data-sticky-atc-label]');
    if (!previewContainer || !qtyEl || !labelEl) return;

    const labelPreview = document.querySelector('#label-preview');

    /* --- Color mapping (same as cart-item-label-preview.liquid) --- */
    const colorMap = {
      Blanc: '#FFFFFF', Noir: '#000000', Bleu: '#318CE7', Vert: '#008000',
      Rouge: '#FF0000', Violet: '#bf1cff', Rose: '#FF69B4', Jaune: '#FFD700',
      Orange: '#FF8C00', Gris: '#808080'
    };

    /* --- Emoji CDN base --- */
    const emojiCdn = 'https://cdn.shopify.com/s/files/1/0832/6329/1737/files/';

    function slugifyEmoji(name) {
      return (name || '').toLowerCase()
        .replace(/\s+/g, '_').replace(/[éèê]/g, 'e').replace(/[àâ]/g, 'a')
        .replace(/[î]/g, 'i').replace(/[ô]/g, 'o').replace(/[ùû]/g, 'u')
        .replace(/ç/g, 'c').replace(/'/g, '_').replace(/-/g, '_') + '.png';
    }

    /* --- Helpers to read current state from the product page --- */
    function getSelectedQuantity() {
      /* Try Globo swatch: checked radio with data-name="Quantité" parent */
      const checkedQty = document.querySelector('.name-option[data-name="Quantité"]');
      if (checkedQty) {
        const parent = checkedQty.parentElement;
        const checked = parent?.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
      }
      /* Fallback: select dropdown */
      const sel = document.querySelector('select[data-name="Quantité"], select[name*="Quantité"]');
      if (sel) return sel.value;
      return '';
    }

    function getSelectedOption(nameSubstring) {
      /* Globo radios */
      const legend = document.querySelector(`.name-option[data-name="${nameSubstring}"]`);
      if (legend) {
        const parent = legend.parentElement;
        const checked = parent?.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value || '';
      }
      /* Dawn selects inside variant-selects */
      const selects = document.querySelectorAll('variant-selects select');
      for (const s of selects) {
        const optName = (s.dataset.optionName || s.name || '').toLowerCase();
        if (optName.includes(nameSubstring.toLowerCase())) return s.value;
      }
      return '';
    }

    function getCurrentShape() {
      const shapeInput = document.getElementById('shapeInput');
      if (shapeInput) {
        const raw = (shapeInput.value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
        if (raw === 'carre') return 'carre';
        if (raw === 'rond') return 'rond';
      }
      return 'rectangle';
    }

    function getFont() {
      const sel = document.getElementById('police-');
      if (!sel) return "'Myriad Pro', sans-serif";
      const fonts = {
        'Myriad Pro': "'Myriad Pro', sans-serif",
        'Impact': "'Impact', sans-serif",
        'Georgia': "'Georgia', serif",
        'Cambria': "'Georgia', serif",
      };
      return fonts[sel.value] || 'sans-serif';
    }

    function getEmojiData() {
      const checked = document.querySelector('input[name="properties[Emoji]"]:checked');
      if (!checked) return { src: '', name: '' };
      const img = checked.nextElementSibling?.querySelector('img');
      return {
        src: img?.currentSrc || img?.src || '',
        name: checked.value || '',
      };
    }

    function resolveColor(optName) {
      const val = getSelectedOption(optName);
      return colorMap[val] || '';
    }

    /* --- Build the mini preview HTML (matches cart-item-label-preview) --- */
    function buildPreview() {
      const input1 = document.querySelector('#prénom-nom-');
      const input2 = document.querySelector('#prénom-nom-2');
      const name1 = (input1?.value || '').trim();
      const name2 = (input2?.value || '').trim();
      const shape = getCurrentShape();
      const font = getFont();
      const emoji = getEmojiData();
      const hasEmoji = emoji.name && emoji.name !== 'Sans' && emoji.name !== '';

      let bgColor = resolveColor("Couleur de fond") || '#FFFFFF';
      let textColor = resolveColor("Couleur d'écriture") || resolveColor("Couleur d\u2019écriture") || '#FF0000';

      /* Readability fix */
      if (bgColor === '#000000' && textColor === '#000000') textColor = '#FFFFFF';
      if (bgColor === '#FFFFFF' && textColor === '#FFFFFF') textColor = '#000000';

      const borderColor = bgColor === '#FFFFFF' ? '#dddddd' : bgColor;

      /* If no name yet, don't show preview */
      if (!name1 && !name2) {
        previewContainer.hidden = true;
        return;
      }

      const emojiSrc = hasEmoji ? emoji.src || `${emojiCdn}${slugifyEmoji(emoji.name)}` : '';

      const emojiImgTag = hasEmoji
        ? `<img src="${escHtml(emojiSrc)}" alt="${escHtml(emoji.name)}" class="cart-item-label-preview__emoji" loading="lazy" onerror="this.style.display='none'" style="width:1.6rem;height:1.6rem;object-fit:contain;flex-shrink:0;">`
        : '';

      const emojiSquareTag = hasEmoji
        ? `<img src="${escHtml(emojiSrc)}" alt="${escHtml(emoji.name)}" class="cart-item-label-preview__emoji-square" loading="lazy" onerror="this.style.display='none'">`
        : '';

      const emojiCircleTag = hasEmoji
        ? `<img src="${escHtml(emojiSrc)}" alt="${escHtml(emoji.name)}" class="cart-item-label-preview__emoji-circle" loading="lazy" onerror="this.style.display='none'">`
        : '';

      let innerHtml = '';

      if (shape === 'rectangle') {
        innerHtml = `
          <div class="cart-item-label-preview cart-item-label-preview--rectangle"
               style="--bg-color:${bgColor};--text-color:${textColor};--font-family:${font};--border-color:${borderColor};">
            <div class="cart-item-label-preview__rect">
              ${emojiImgTag}
              <span class="cart-item-label-preview__text">${escHtml(name1)}</span>
              ${emojiImgTag}
            </div>
          </div>`;
      } else if (shape === 'carre') {
        innerHtml = `
          <div class="cart-item-label-preview cart-item-label-preview--carre"
               style="--bg-color:${bgColor};--text-color:${textColor};--font-family:${font};--border-color:${borderColor};">
            <div class="cart-item-label-preview__square ${!hasEmoji ? 'no-emoji' : ''}">
              ${emojiSquareTag}
              <div class="cart-item-label-preview__square-lines">
                <div class="cart-item-label-preview__line">${escHtml(name1 || 'Ligne 1')}</div>
                <div class="cart-item-label-preview__line">${escHtml(name2 || 'Ligne 2')}</div>
              </div>
            </div>
          </div>`;
      } else if (shape === 'rond') {
        const uid = 'sticky-' + Date.now();
        innerHtml = `
          <div class="cart-item-label-preview cart-item-label-preview--rond"
               style="--bg-color:${bgColor};--text-color:${textColor};--font-family:${font};--border-color:${borderColor};">
            <div class="cart-item-label-preview__circle">
              ${emojiCircleTag}
              <svg class="cart-item-label-preview__circle-svg" viewBox="0 0 200 200" aria-hidden="true">
                <defs>
                  <path id="topArc-${uid}" d="M 15,100 A 85,85 0 0,1 185,100" />
                  <path id="bottomArc-${uid}" d="M 15,100 A 85,85 0 0,0 185,100" />
                </defs>
                <text class="cart-item-label-preview__circle-text">
                  <textPath href="#topArc-${uid}" startOffset="50%" text-anchor="middle" dy="-6">${escHtml(name1 || 'Ligne 1')}</textPath>
                </text>
                ${name2 ? `<text class="cart-item-label-preview__circle-text">
                  <textPath href="#bottomArc-${uid}" startOffset="50%" text-anchor="middle" dy="18">${escHtml(name2)}</textPath>
                </text>` : ''}
              </svg>
            </div>
          </div>`;
      }

      labelEl.innerHTML = `<div class="cart-item-label-wrapper">${innerHtml}</div>`;

      /* Quantity */
      const qty = getSelectedQuantity();
      if (qty) {
        qtyEl.innerHTML = `${escHtml(qty)}<span style="font-size:1.1rem;opacity:0.7;margin-left:1px;">x</span>`;
      } else {
        qtyEl.textContent = '';
      }

      previewContainer.hidden = false;
    }

    function escHtml(str) {
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }

    /* --- Attach listeners --- */
    document.querySelectorAll('#prénom-nom-, #prénom-nom-2').forEach((input) => {
      input.addEventListener('input', buildPreview);
    });

    /* Variant / option changes (Globo radios, selects, etc.) */
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!t) return;
      if (t.closest('variant-selects') || t.closest('.globo-variant-selector') || t.name?.includes('properties[')) {
        setTimeout(buildPreview, 60);
      }
    });

    /* Shape changes */
    const shapeInput = document.getElementById('shapeInput');
    if (shapeInput) {
      shapeInput.addEventListener('change', () => setTimeout(buildPreview, 60));
    }
    document.addEventListener('shape:change', () => setTimeout(buildPreview, 60));

    /* Font change */
    const fontSel = document.getElementById('police-');
    if (fontSel) fontSel.addEventListener('change', () => setTimeout(buildPreview, 60));

    /* PubSub variant change */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(buildPreview, 100));
    }

    /* Initial build */
    buildPreview();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyPreview);
  } else {
    initStickyPreview();
  }
})();
